/**
 * 初始化界面组件
 * 在首次使用或数据库不存在时显示初始化进度
 */

import React, { useEffect, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import {
  checkMainDBExists,
  initMainDB,
  initUserDB,
  checkUserDBExists,
  diagnoseIndexedDB,
  type InitializationStatus
} from '../services/dbManager'
import {
  getCurrentUserUuid,
  setCurrentUserUuid,
  addOrUpdateUser,
  getAllUsers,
  type UserInfo
} from '../services/userManager'

interface InitializationViewProps {
  onComplete: (userUuid: string) => void
}

type InitPhase = 'checking' | 'welcome' | 'initializing' | 'user_selection' | 'complete'

const InitializationView: React.FC<InitializationViewProps> = ({ onComplete }) => {
  const [phase, setPhase] = useState<InitPhase>('checking')
  const [status, setStatus] = useState<InitializationStatus>({
    stage: 'checking',
    progress: 0,
    message: '正在检查数据库状态...'
  })
  const [existingUsers, setExistingUsers] = useState<UserInfo[]>([])
  const [inputUuid, setInputUuid] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isVerifying, setIsVerifying] = useState(false)

  useEffect(() => {
    checkInitializationStatus()
  }, [])

  const checkInitializationStatus = async () => {
    try {
      // 先诊断 IndexedDB 是否可用
      const diagnosis = await diagnoseIndexedDB()
      if (!diagnosis.available) {
        setError(`IndexedDB 不可用: ${diagnosis.error}\n\n请尝试:\n1. 关闭隐私/无痕模式\n2. 检查浏览器存储权限\n3. 清理浏览器缓存后重试\n4. 换用其他浏览器`)
        setPhase('welcome')
        return
      }

      // 检查公共数据库是否存在
      const mainDBExists = await checkMainDBExists()
      
      if (!mainDBExists) {
        // 数据库不存在，显示欢迎界面
        setPhase('welcome')
        return
      }

      // 数据库存在，检查当前用户
      const currentUserUuid = await getCurrentUserUuid()
      
      if (currentUserUuid) {
        // 有当前用户，检查用户数据库
        const userDBExists = await checkUserDBExists(currentUserUuid)
        
        if (userDBExists) {
          // 用户数据库存在，直接完成
          onComplete(currentUserUuid)
          return
        }
        
        // 用户数据库不存在，需要创建
        setPhase('initializing')
        await initUserDB(currentUserUuid, setStatus)
        setStatus({
          stage: 'complete',
          progress: 100,
          message: '初始化完成！'
        })
        setTimeout(() => onComplete(currentUserUuid), 500)
        return
      }

      // 没有当前用户，检查是否有用户列表
      const users = await getAllUsers()
      if (users.length > 0) {
        // 有用户列表，显示用户选择界面
        setExistingUsers(users)
        setPhase('user_selection')
      } else {
        // 没有用户，显示欢迎界面
        setPhase('welcome')
      }
    } catch (err) {
      console.error('检查初始化状态失败:', err)
      setPhase('welcome')
    }
  }

  const handleNewUser = async () => {
    setPhase('initializing')
    setError(null)

    try {
      // 再次检查 IndexedDB
      const diagnosis = await diagnoseIndexedDB()
      if (!diagnosis.available) {
        throw new Error(`IndexedDB 不可用: ${diagnosis.error}`)
      }

      // 初始化公共数据库
      setStatus({
        stage: 'creating_main_db',
        progress: 10,
        message: '正在初始化公共数据库...'
      })
      await initMainDB(setStatus)

      // 创建新用户
      const newUserUuid = uuidv4()
      
      setStatus({
        stage: 'creating_user_db',
        progress: 50,
        message: '正在创建用户数据...'
      })

      // 保存用户信息
      await setCurrentUserUuid(newUserUuid)
      await addOrUpdateUser(newUserUuid)

      // 创建用户数据库
      await initUserDB(newUserUuid, setStatus)

      setStatus({
        stage: 'complete',
        progress: 100,
        message: '初始化完成！欢迎使用无限笔记！'
      })

      setPhase('complete')
      setTimeout(() => onComplete(newUserUuid), 1500)
    } catch (err: any) {
      setError(err.message || '初始化失败')
      setStatus({
        stage: 'error',
        progress: 0,
        message: '初始化失败',
        error: err.message
      })
    }
  }

  const handleSelectUser = async (userUuid: string) => {
    setPhase('initializing')
    setError(null)

    try {
      // 先确保公共数据库存在
      const mainDBExists = await checkMainDBExists()
      if (!mainDBExists) {
        setStatus({
          stage: 'creating_main_db',
          progress: 20,
          message: '正在初始化公共数据库...'
        })
        await initMainDB(setStatus)
      }

      // 检查用户数据库是否存在
      const userDBExists = await checkUserDBExists(userUuid)

      if (!userDBExists) {
        // 用户数据库不存在，创建它
        setStatus({
          stage: 'creating_user_db',
          progress: 50,
          message: '正在恢复用户数据...'
        })
        await initUserDB(userUuid, setStatus)
      }

      // 设置为当前用户
      await setCurrentUserUuid(userUuid)

      setStatus({
        stage: 'complete',
        progress: 100,
        message: '加载完成！'
      })

      setTimeout(() => onComplete(userUuid), 500)
    } catch (err: any) {
      setError(err.message || '加载用户失败')
      setPhase('user_selection')
    }
  }

  const handleLoginWithUuid = async () => {
    if (!inputUuid.trim()) {
      setError('请输入用户 UUID')
      return
    }

    // 验证 UUID 格式
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    if (!uuidPattern.test(inputUuid.trim())) {
      setError('UUID 格式不正确')
      return
    }

    setIsVerifying(true)
    setError(null)

    const targetUuid = inputUuid.trim()

    try {
      // 验证用户是否存在于服务器
      const { verifyUser } = await import('../services/api')
      const result = await verifyUser(targetUuid)

      if (result.success && result.data?.exists) {
        // 用户在服务器上存在
        // 0. 先确保公共数据库存在
        const mainDBExists = await checkMainDBExists()
        if (!mainDBExists) {
          console.log('[InitializationView] 公共数据库不存在，先创建')
          await initMainDB()
        }
        
        // 1. 创建本地用户数据库
        console.log('[InitializationView] 创建用户数据库:', targetUuid)
        await initUserDB(targetUuid)
        
        // 2. 添加到本地用户列表
        await addOrUpdateUser(targetUuid)
        
        // 3. 如果服务器返回了用户数据，保存到本地数据库
        if (result.data.userData) {
          const { saveProjectToUserDB, saveItemsToUserDB } = await import('../services/dbManager')
          const serverData = result.data.userData
          
          if (serverData.projects && serverData.projects.length > 0) {
            console.log('[InitializationView] 从服务器加载了', serverData.projects.length, '个项目')
            
            // 保存项目到本地数据库
            for (const project of serverData.projects) {
              await saveProjectToUserDB(targetUuid, project)
              if (project.items && project.items.length > 0) {
                const itemsWithProjectId = project.items.map((item: any) => ({
                  ...item,
                  projectId: project.id
                }))
                await saveItemsToUserDB(targetUuid, itemsWithProjectId)
              }
            }
          }
        }
        
        // 4. 选择该用户
        await handleSelectUser(targetUuid)
      } else {
        setError('服务器上不存在该用户，请检查 UUID 是否正确')
      }
    } catch (err: any) {
      setError('验证用户失败: ' + (err.message || '网络错误'))
    } finally {
      setIsVerifying(false)
    }
  }

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // 检查中
  if (phase === 'checking') {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-pink-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-pink-300 border-t-pink-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">正在检查数据库状态...</p>
        </div>
      </div>
    )
  }

  // 欢迎界面
  if (phase === 'welcome') {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-pink-50 to-purple-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-br from-[#ff5c5c] to-[#ff8a8a] p-8 text-center text-white">
            <div className="w-20 h-20 mx-auto mb-4 bg-white/20 rounded-2xl flex items-center justify-center">
              <img src="/logo.png" alt="Logo" className="w-16 h-16 object-contain" />
            </div>
            <h1 className="text-3xl font-bold mb-2">欢迎使用无限笔记</h1>
            <p className="text-white/80">在有限生命里，遇见那个有趣的自己</p>
          </div>

          {/* Content */}
          <div className="p-8">
            <div className="space-y-4">
              <button
                onClick={handleNewUser}
                className="w-full py-4 bg-gradient-to-r from-[#ff5c5c] to-[#ff8a8a] text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-300 hover:scale-[1.02]"
              >
                🎉 创建新用户
              </button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500">或</span>
                </div>
              </div>

              <div className="space-y-3">
                <input
                  type="text"
                  value={inputUuid}
                  onChange={(e) => setInputUuid(e.target.value)}
                  placeholder="输入已有用户 UUID"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-300 focus:border-transparent"
                  onKeyDown={(e) => e.key === 'Enter' && handleLoginWithUuid()}
                />
                <button
                  onClick={handleLoginWithUuid}
                  disabled={isVerifying}
                  className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                  {isVerifying ? '验证中...' : '登录已有用户'}
                </button>
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
                  {error}
                </div>
              )}
            </div>

            <p className="mt-6 text-xs text-gray-400 text-center">
              用户 UUID 是您数据的唯一标识，请妥善保管
            </p>

            <div className="mt-6 pt-4 border-t border-gray-100 text-center text-xs text-gray-400">
              <a
                href="https://beian.miit.gov.cn"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-gray-600 transition-colors"
              >
                蜀ICP备2020028680号-1
              </a>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // 用户选择界面
  if (phase === 'user_selection') {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-pink-50 to-purple-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="bg-gradient-to-br from-[#ff5c5c] to-[#ff8a8a] p-6 text-center text-white flex-shrink-0">
            <h1 className="text-2xl font-bold mb-1">选择用户</h1>
            <p className="text-white/80 text-sm">选择一个已有用户或创建新用户</p>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto flex-1">
            {/* 已有用户列表 */}
            <div className="space-y-3 mb-6">
              {existingUsers.map((user) => (
                <button
                  key={user.userUuid}
                  onClick={() => handleSelectUser(user.userUuid)}
                  className="w-full p-4 border border-gray-200 rounded-xl text-left hover:border-pink-300 hover:bg-pink-50 transition-all"
                >
                  <div className="font-medium text-gray-900">
                    {user.nickname || '未命名用户'}
                  </div>
                  <div className="text-xs text-gray-500 font-mono mt-1 truncate">
                    {user.userUuid}
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    最后登录: {formatDate(user.lastLoginTime)}
                  </div>
                </button>
              ))}
            </div>

            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">或</span>
              </div>
            </div>

            <button
              onClick={handleNewUser}
              className="w-full py-3 bg-gradient-to-r from-[#ff5c5c] to-[#ff8a8a] text-white rounded-xl font-semibold hover:shadow-lg transition-all"
            >
              🎉 创建新用户
            </button>

            <div className="mt-4 space-y-3">
              <input
                type="text"
                value={inputUuid}
                onChange={(e) => setInputUuid(e.target.value)}
                placeholder="输入其他用户 UUID"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-300"
                onKeyDown={(e) => e.key === 'Enter' && handleLoginWithUuid()}
              />
              <button
                onClick={handleLoginWithUuid}
                disabled={isVerifying}
                className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                {isVerifying ? '验证中...' : '登录其他用户'}
              </button>
            </div>

            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
                {error}
              </div>
            )}

            <div className="mt-6 pt-4 border-t border-gray-100 text-center text-xs text-gray-400">
              <a
                href="https://beian.miit.gov.cn"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-gray-600 transition-colors"
              >
                蜀ICP备2020028680号-1
              </a>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // 初始化中
  if (phase === 'initializing' || phase === 'complete') {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-pink-50 to-purple-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-br from-[#ff5c5c] to-[#ff8a8a] p-8 text-center text-white">
            <div className="w-20 h-20 mx-auto mb-4 bg-white/20 rounded-2xl flex items-center justify-center">
              {status.stage === 'complete' ? (
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : status.stage === 'error' ? (
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin" />
              )}
            </div>
            <h1 className="text-2xl font-bold mb-2">
              {status.stage === 'complete' ? '初始化完成' : status.stage === 'error' ? '初始化失败' : '正在初始化'}
            </h1>
          </div>

          {/* Progress */}
          <div className="p-8">
            <div className="mb-4">
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#ff5c5c] to-[#ff8a8a] transition-all duration-500"
                  style={{ width: `${status.progress}%` }}
                />
              </div>
            </div>

            <p className="text-center text-gray-600">{status.message}</p>

            {status.error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
                {status.error}
              </div>
            )}

            {status.stage === 'error' && (
              <button
                onClick={() => setPhase('welcome')}
                className="mt-4 w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
              >
                返回
              </button>
            )}
          </div>
        </div>
      </div>
    )
  }

  return null
}

export default InitializationView
